import { DateTime } from 'luxon';
import type { CouchUserCoachData } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';

/**
 * The pure decision behind "does this user see the COACH-specific paywall
 * message?" (#4393 Surface A). Outside the component so it is unit-testable
 * without Angular, like `purchase-offer-policy.ts` next door.
 *
 * The message claims the reader is STILL CONNECTED to their coach, which is
 * what makes a false positive expensive: `coachDetachClient` leaves a DETACHED
 * client's `coachData` block in place (set to `Terminated`), so by the block
 * alone they look exactly like a lapsed one. Hence the liveness requirement.
 * Full reasoning in design-docs/coach-subscription-lapse.md.
 */
export interface CoachLapseNoticeState {
  /**
   * Whether the user currently HAS Premium access (`role >= AuthRole.Paid`).
   *
   * Deliberately not the page's `expired` flag, which comes from
   * `getAuthRoleAndExpiry`'s `subscriptionExpired`. That flag turns true only
   * when the user's OWN paid role expired, and a coach-revoked client's role
   * goes straight from Paid to Unknown at the moment the login refresh
   * recomputes it — so `subscriptionExpired` is false for exactly the people
   * this card is for. (That is also why the generic "your subscription is
   * inactive" card never rendered for them either: they see nothing at all
   * today, which is a stronger version of the problem #4393 exists to fix.)
   */
  hasPremiumAccess: boolean;
  /**
   * The coach-granted block from the login result, or undefined when the user
   * never had a coach (or `auth.couch` is null, i.e. offline).
   */
  coachData: CouchUserCoachData | undefined;
  /**
   * True ONLY when the local `as-client` document was read successfully AND
   * still names a coach. A failed or unavailable read is `false` — see the
   * header above for why that is not merely a default.
   */
  relationshipConfirmedLive: boolean;
  /** Evaluation time, passed in so the rule stays pure and testable. */
  now: DateTime;
}

/**
 * Whether to show the coach-specific paywall message.
 *
 * ## The wording is deliberately vague about WHY, and must stay that way (#4530)
 *
 * There are now two reasons a client's coach-granted access can stop: the
 * coach's own plan ended, or the coach parked this one client (#4530). This
 * function cannot tell them apart, and that is not an oversight to fix — the
 * parked state lives on the coach's own client record, in the COACH's database,
 * which the client's app cannot read. Both cases arrive here looking identical:
 * a coach block that no longer grants.
 *
 * The decision (user, 2026-08-01) is that the client does not need to know
 * which it was. So the message says their coach PAUSED their access and stops
 * there, which is true either way. It deliberately does not say the coach's
 * plan ended, and it must not be "improved" back into saying so: for a parked
 * client that would be a false statement about somebody else's payments, and
 * one they might well repeat back to their coach.
 *
 * If a future change ever does make the reason visible here, that is a reason
 * to consider better wording — not a reason to assume the lapse case.
 *
 * Note it does not merely REPLACE the generic "your subscription is inactive"
 * card — for these users that card never rendered in the first place (see
 * `hasPremiumAccess` above). The two cards are for different people: the generic
 * one for somebody whose own paid subscription ran out, this one for somebody
 * whose coach stopped paying. Neither shows to a brand-new user who never had
 * a subscription at all, which is why the generic card's own gate is left alone.
 *
 * Every term is a requirement, written as an explicit conjunction so that a
 * future edit has to decide about each one rather than silently drop it.
 *
 * The "not currently granting" term reuses `isInGrace` — the ONE access
 * predicate this codebase has, shared with Phase 1's mirror — rather than
 * comparing dates here. That matters more than it looks: the existing client
 * population still holds the literal string `'never'` as an expiry, which no
 * naive date comparison handles, and `isInGrace` is where that is already
 * understood. It also means a Legacy (admin-granted) coach's client, whose grant
 * is `NEVER_EXPIRES`, is never shown this message.
 */
export function shouldShowCoachLapseNotice(state: CoachLapseNoticeState): boolean {
  const grant = state.coachData;
  if (!grant) return false;

  const currentlyGranting = isInGrace(
    grant.subscriptionStatus,
    grant.expiresDate_utc,
    state.now.toMillis(),
  );

  return !state.hasPremiumAccess
    && !currentlyGranting
    && state.relationshipConfirmedLive;
}
